import React from 'react';
import { formatDate, formatDateTime } from '@/lib/formatDate';
import { displayAge } from '@/lib/patientAge';
import { groupPhone, Letterhead, PrintFooter, PrintStyles, SignatureBlock } from '@/components/Print/PrintParts';
import type { HeaderConfig } from '@/components/Print/UnifiedPrintModal';

// ── Types ───────────────────────────────────────────────────────────────────

interface InvoiceItem {
  service_name?: string;
  name?: string;
  category?: string;
  quantity?: number;
  unit_price?: number;
  price?: number;
}

export interface InvoiceData {
  // IDs — used by UnifiedPrintModal to fetch patient/doctor/clinic info
  patient_id?: string;
  doctor_id?:  string;
  clinic_id?:  string;

  // Joined fields from backend SELECT — used directly when API fetch not possible
  doctor_name?:    string;
  patient_name?:   string;
  patient_phone?:  string;
  patient_mrn?:    string;
  clinic_name?:    string;
  clinic_address?: string;

  /** A receipt that isn't a bill (an advance deposit): its title, and no visit date */
  doc_title?:      string;
  /** Who the money went to or came from, in place of "Received from" (e.g. "Paid to" on a refund) */
  party_label?:    string;
  /** Money handed to the patient: a line for them to sign that they received it */
  recipient_signs?: boolean;
  /** Label before the payment mode, in place of "Payment" (e.g. "Refunded") */
  payment_label?:  string;
  invoice_number?: string;
  invoice_date?:   string;
  visit_date?:     string;
  invoice_status?: string;

  subtotal?:        number;
  discount_amount?: number;
  tax_amount?:      number;
  total_amount?:    number;
  balance_due?:     number;  // from patient_ledger via API — never computed here
  paid_amount?:     number;  // canonical name — no paidAmount alias
  payment_mode?:    string;
  notes?:           string;
  items?:           InvoiceItem[];
}

interface ClinicInfo  { name?: string; address?: string; phone?: string; gstin?: string; logo?: string | null; letterhead?: string | null; }
interface DoctorInfo  { name?: string; }
interface PatientInfo { name?: string; patient_name?: string; mrn?: string; phone?: string; dob?: string; date_of_birth?: string; gender?: string; age?: number; }
interface Settings    { profile?: DoctorInfo; }
interface CurrentUser { name?: string; }

export interface InvoiceContentProps {
  data:         InvoiceData | null;
  translations: Record<string, string>;
  language?:    string;
  clinicInfo?:  ClinicInfo  | null;
  doctorInfo?:  DoctorInfo  | null;
  patientInfo?: PatientInfo | null;
  settings?:    Settings    | null;
  currentUser?: CurrentUser | null;
  headerConfig?: HeaderConfig;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

// "₹1,000.00" — Indian digit grouping
const fmt = (amount?: number | null): string =>
    `₹${Number(amount ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const n   = (v?: number | null): number => Number(v ?? 0);

// ── Component ────────────────────────────────────────────────────────────────

export function InvoiceContent({
                                 data, translations: t,
                                 clinicInfo, doctorInfo, patientInfo, settings, currentUser, headerConfig,
                               }: InvoiceContentProps) {

  if (!data) return null;

  const doctorName    = settings?.profile?.name   ?? doctorInfo?.name    ?? data.doctor_name;

  // Patient: from fetched patientInfo → joined fields on data
  const pName   = patientInfo?.name    ?? patientInfo?.patient_name ?? data.patient_name  ?? '—';
  const pMrn    = patientInfo?.mrn     ?? data.patient_mrn;
  const pPhone  = patientInfo?.phone   ?? data.patient_phone;
  const pAge    = displayAge(patientInfo?.age, patientInfo?.dob ?? patientInfo?.date_of_birth);
  const pDemo   = [pAge, patientInfo?.gender].filter(Boolean).join(' · ');
  const pIds    = [pMrn && `MRN ${pMrn}`, pPhone && `Ph ${groupPhone(pPhone)}`].filter(Boolean).join(' · ');

  const status      = data.invoice_status?.toUpperCase();
  const isCancelled = status === 'CANCELLED';
  const isDraft     = status === 'DRAFT';

  const invoiceNumber = data.invoice_number ?? '';  // a draft has none; its DRAFT label says so
  const visitDate     = data.doc_title ? undefined : (data.visit_date ?? data.invoice_date);
  const docTitle      = data.doc_title ?? (n(data.paid_amount) > 0 ? 'Bill-cum-Receipt' : (t.invoice ?? 'Invoice'));
  const muted         = { color: '#555' };

  return (
      <div className="p-doc">
        <PrintStyles />

        <Letterhead
            headerConfig={headerConfig}
            clinic={{ name: clinicInfo?.name, address: clinicInfo?.address, phone: clinicInfo?.phone, logo: clinicInfo?.logo, letterhead: clinicInfo?.letterhead }}
            right={clinicInfo?.gstin && <div className="p-small p-muted">GSTIN {clinicInfo.gstin}</div>}
        />

        {/* Title, with the bill's state beside it rather than a watermark over the text */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8mm', marginTop: '5mm' }}>
          <div style={{ fontSize: '18px', fontWeight: 700 }}>
            {docTitle} {invoiceNumber}
            {(isCancelled || isDraft) && (
                <span style={{ marginLeft: '3mm', padding: '0.5mm 2.5mm', border: '1.5px solid #111', fontSize: '11px', letterSpacing: '0.04em', verticalAlign: 'middle' }}>
                  {isCancelled ? 'CANCELLED' : 'DRAFT'}
                </span>
            )}
          </div>
          {data.invoice_date && (
              <div style={{ textAlign: 'right', fontSize: '12px', ...muted }}>
                {data.doc_title ? 'Date' : 'Invoice date'}
                <div style={{ color: '#111', fontSize: '14px', fontWeight: 700 }}>{formatDate(data.invoice_date)}</div>
              </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6mm', marginTop: '4mm', padding: '3mm 0', borderTop: '1px solid #999', borderBottom: '1px solid #999', fontSize: '12.5px' }}>
          <div>
            <div style={muted}>{data.party_label ?? (data.doc_title ? 'Received from' : 'Billed to')}</div>
            <b style={{ fontSize: '14px' }}>{pName}</b>{pDemo && <> · {pDemo}</>}
            {pIds && <div>{pIds}</div>}
          </div>
          <div>
            {doctorName && <><div style={muted}>Consultant</div><div>Dr. {doctorName}</div></>}
            {visitDate && <div><span style={muted}>Visit date</span> {formatDate(visitDate)}</div>}
          </div>
        </div>

        {/* Line items */}
        {!!data.items?.length && (
            <table className="p-table" style={{ marginTop: '5mm' }}>
              <thead>
                <tr>
                  <th style={{ width: '6mm' }}>#</th>
                  <th>{t.service ?? 'Service'}</th>
                  <th>{t.category ?? 'Type'}</th>
                  <th className="p-num">{t.quantity ?? 'Qty'}</th>
                  <th className="p-num">{t.unitPrice ?? 'Rate'}</th>
                  <th className="p-num">{t.totalAmount ?? 'Amount'}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item, idx) => {
                  const qty   = Number(item.quantity ?? 1);
                  const price = item.unit_price ?? item.price ?? 0;
                  return (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td style={{ fontWeight: 700 }}>{item.service_name ?? item.name ?? '—'}</td>
                        <td style={{ textTransform: 'capitalize' }}>{item.category ?? 'Service'}</td>
                        <td className="p-num">{Number.isInteger(qty) ? qty : qty.toFixed(2)}</td>
                        <td className="p-num">{fmt(price)}</td>
                        <td className="p-num">{fmt(qty * price)}</td>
                      </tr>
                  );
                })}
              </tbody>
            </table>
        )}

        {/* Amounts */}
        <div style={{ margin: '4mm 0 0 auto', width: '80mm', fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>
          <AmtRow label={t.subtotal ?? 'Subtotal'} value={fmt(data.subtotal ?? data.total_amount)} />
          {n(data.discount_amount) > 0 && <AmtRow label="Discount" value={`- ${fmt(data.discount_amount)}`} />}
          {n(data.tax_amount) > 0 && <AmtRow label="Tax" value={fmt(data.tax_amount)} />}
          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1.5px solid #111', marginTop: '1mm', paddingTop: '2mm', fontSize: '15px', fontWeight: 700 }}>
            <span>{t.total ?? 'Total'}</span>
            <span style={isCancelled ? { textDecoration: 'line-through' } : undefined}>{fmt(data.total_amount)}</span>
          </div>
          {isCancelled ? (
              <div style={{ ...muted, paddingTop: '1mm' }}>Cancelled: nothing is payable on this bill</div>
          ) : (
              <>
                {n(data.paid_amount) > 0 && <AmtRow label={t.amountPaid ?? 'Amount Paid'} value={fmt(data.paid_amount)} />}
                {n(data.balance_due) > 0 && <AmtRow label={t.balanceDue ?? 'Balance Due'} value={fmt(data.balance_due)} bold />}
              </>
          )}
        </div>

        {(data.payment_mode || data.notes) && (
            <div style={{ marginTop: '6mm', fontSize: '12px' }}>
              {data.payment_mode && <div><b>{data.payment_label ?? 'Payment'}</b> · {data.payment_mode}</div>}
              {data.notes && <div><b>Notes</b> · {data.notes}</div>}
            </div>
        )}

        {data.recipient_signs ? (
            <div className="p-sign" style={{ justifyContent: 'space-between' }}>
              <div><b>Received by</b><div>{pName}</div></div>
              <div><b>Authorised signatory</b>{currentUser?.name && <div>{currentUser.name}</div>}</div>
            </div>
        ) : (
            <SignatureBlock>
              <b>Authorised signatory</b>
              {currentUser?.name && <div>{currentUser.name}</div>}
            </SignatureBlock>
        )}

        <PrintFooter
            left={[clinicInfo?.name, invoiceNumber && `${data.doc_title ?? t.invoice ?? 'Invoice'} ${invoiceNumber}`].filter(Boolean).join(' · ')}
            printedOn={formatDateTime(new Date())}
        />
      </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function AmtRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1mm 0', fontWeight: bold ? 700 : undefined }}>
        <span>{label}</span>
        <span>{value}</span>
      </div>
  );
}
